import { useState, useEffect, useMemo } from 'react';
import { X, Loader, BarChart3, Globe, TrendingUp, Award, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { VoteUserBreakdown, VoteHistoryResponse, VotePeriod } from '../types/vote';

/** Pick the best granularity for a given period */
const granularityForPeriod = (period: VotePeriod): string => {
  switch (period) {
    case 'today':
    case 'week':
      return 'hour';
    default:
      return 'day';
  }
};
import { votesApiService } from '../services/votesApi';
import { usePlayerFromUsername } from '../hooks/usePlayerFromUsername';
import { PlayerProfile } from './PlayerProfile';
import { VoteChart } from './VoteChart';
import { SkeletonLoader } from './SkeletonLoader';
import { SectionHeader } from './ui/SectionHeader';
import { cn } from '../utils/cn';

interface VoteUserDrilldownModalProps {
  username: string;
  period: VotePeriod;
  isOpen: boolean;
  onClose: () => void;
}

const ServiceBar = ({ serviceName, voteCount, maxCount }: { serviceName: string; voteCount: number; maxCount: number }) => {
  const widthPercent = maxCount > 0 ? (voteCount / maxCount) * 100 : 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
          <span className="text-white truncate">{serviceName}</span>
        </div>
        <span className="text-glass-300 font-mono text-xs tabular-nums ml-2 flex-shrink-0">{voteCount.toLocaleString()}</span>
      </div>
      <div className="w-full h-2 bg-glass-200/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${widthPercent}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="h-full rounded-full bg-accent-400"
        />
      </div>
    </div>
  );
};

export const VoteUserDrilldownModal = ({ username, period, isOpen, onClose }: VoteUserDrilldownModalProps) => {
  const { uuid, loading: uuidLoading } = usePlayerFromUsername(username);
  const [userBreakdown, setUserBreakdown] = useState<VoteUserBreakdown | null>(null);
  const [history, setHistory] = useState<VoteHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !username) return;

    setLoading(true);
    setError(null);

    Promise.all([
      votesApiService.getByUser(username, period),
      votesApiService.getHistory(period, granularityForPeriod(period), username),
    ])
      .then(([breakdown, hist]) => {
        setUserBreakdown(breakdown);
        setHistory(hist);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load vote data');
      })
      .finally(() => setLoading(false));
  }, [isOpen, username, period]);

  const maxServiceVotes = useMemo(() => {
    if (!userBreakdown?.services?.length) return 0;
    return Math.max(...userBreakdown.services.map(s => s.vote_count));
  }, [userBreakdown]);

  const sortedServices = useMemo(() => {
    if (!userBreakdown?.services) return [];
    return [...userBreakdown.services].sort((a, b) => b.vote_count - a.vote_count);
  }, [userBreakdown]);

  // Calculate favorite service
  const favoriteService = sortedServices.length > 0 ? sortedServices[0] : null;

  // Calculate avg votes/day from history
  const avgPerDay = useMemo(() => {
    if (!history?.data?.length) return 0;
    const total = history.data.reduce((sum, d) => sum + (d.vote_count ?? 0), 0);
    const daysWithVotes = history.data.filter(d => (d.vote_count ?? 0) > 0).length;
    return daysWithVotes > 0 ? (total / daysWithVotes) : 0;
  }, [history]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="modal-panel max-w-4xl"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.3 }}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3 min-w-0">
              <span className="icon-tile icon-tile-warning">
                <Award className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <h2 className="modal-title flex items-center gap-3 min-w-0">
                  {uuid && !uuidLoading ? (
                    <PlayerProfile uuid={uuid} name={username} showUuid={false} showAvatar={true} inline={true} />
                  ) : uuidLoading ? (
                    <span className="text-glass-400 animate-pulse">Loading player...</span>
                  ) : (
                    <span>{username}</span>
                  )}
                </h2>
                <p className="modal-subtitle">
                  Vote history and breakdown
                  {uuid && <span className="font-mono ml-2 text-xs">{uuid}</span>}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="modal-body space-y-6">
            {loading && (
              <div className="space-y-6" aria-busy="true">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="subcard p-3 space-y-2">
                      <SkeletonLoader className="h-3 w-20" />
                      <SkeletonLoader className="h-6 w-14" />
                    </div>
                  ))}
                </div>
                <div className="subcard p-4">
                  <SkeletonLoader className="h-4 w-40 mb-4" />
                  <SkeletonLoader className="h-48 w-full rounded-lg" />
                </div>
                <div className="flex items-center justify-center py-2 text-glass-400">
                  <Loader className="w-5 h-5 animate-spin text-accent-400" />
                </div>
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                <AlertCircle />
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && userBreakdown && (
              <>
                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatMini
                    label="Total Votes"
                    value={(userBreakdown.total_votes ?? (userBreakdown as any).vote_count ?? 0).toLocaleString()}
                    icon={<BarChart3 className="w-4 h-4" />}
                    tone="icon-tile-accent"
                  />
                  <StatMini
                    label="Services Used"
                    value={sortedServices.length.toString()}
                    icon={<Globe className="w-4 h-4" />}
                    tone="icon-tile-info"
                  />
                  <StatMini
                    label="Avg Votes/Active Day"
                    value={avgPerDay.toFixed(1)}
                    icon={<TrendingUp className="w-4 h-4" />}
                    tone="icon-tile-success"
                  />
                  <StatMini
                    label="Favorite Site"
                    value={favoriteService?.service_name || 'N/A'}
                    icon={<Award className="w-4 h-4" />}
                    tone="icon-tile-warning"
                    small
                  />
                </div>

                {/* Daily vote chart for this user */}
                <VoteChart
                  data={history?.data || []}
                  title={`${username}'s Daily Votes`}
                  height={220}
                  accentColor="rgb(var(--accent-t-400-rgb))"
                  emptyMessage="No vote history available for this period."
                  bare
                />

                {/* Service breakdown */}
                <div className="subcard p-4">
                  <SectionHeader
                    title="Votes by Service"
                    icon={Globe}
                    actions={<span className="text-xs text-glass-400 tabular-nums">{sortedServices.length} service{sortedServices.length !== 1 ? 's' : ''}</span>}
                  />

                  {sortedServices.length === 0 ? (
                    <p className="text-sm text-glass-400">No service data available.</p>
                  ) : (
                    <div className="space-y-3">
                      {sortedServices.map((service) => (
                        <ServiceBar
                          key={service.service_name}
                          serviceName={service.service_name}
                          voteCount={service.vote_count}
                          maxCount={maxServiceVotes}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const StatMini = ({ label, value, icon, small, tone }: { label: string; value: string; icon: React.ReactNode; small?: boolean; tone?: string }) => (
  <div className="subcard p-3 flex items-start justify-between gap-3 min-w-0">
    <div className="min-w-0 flex-1">
      <div className="eyebrow truncate">{label}</div>
      <div className={cn("font-semibold text-white tracking-tight tabular-nums mt-1 truncate", small ? "text-sm" : "text-xl")} title={value}>
        {value}
      </div>
    </div>
    <span className={cn('icon-tile', tone ?? 'icon-tile-neutral')}>{icon}</span>
  </div>
);
